import { useState, useRef } from "react";
import {
  Button,
  Box,
  IconButton,
  TextField,
  InputAdornment,
} from "@mui/material";
import {
  ChevronLeft,
  ChevronRight,
  Clear as ClearIcon,
  Close as CloseIcon,
  Menu as MenuIcon,
  Person as LoginIcon,
  Search as SearchIcon,
  Info as InfoIcon,
} from "@mui/icons-material";
import { MenuDropUp } from "./MenuDropUp";
import { AuthenticationControlsProps } from "../../../hooks/ui/useAuthenticationControls";
import { SearchControlsProps } from "../../../hooks/ui/useSearchControls";

interface NavigationControlsProps {
  regularLocationPage: number;
  totalPages: number;
  onPageChange: (direction: number) => void;
  onLocationEditionClick: () => void;
  canAccessLocationContentEdition: boolean; // BYI Supervisor - for Edit Location
  authentication: AuthenticationControlsProps;
  search: SearchControlsProps;
  selectedLocationIdForClear?: number | null;
  onClearLocationClick?: () => void;
  selectedLocationId?: number | null;
  onInfoClick?: () => void;
  isSupervisor?: boolean;
  onHeatSearch?: (heatName: string) => void;
}

export const NavigationControls = ({
  regularLocationPage,
  totalPages,
  onPageChange,
  onLocationEditionClick,
  canAccessLocationContentEdition,
  authentication,
  search,
  selectedLocationIdForClear,
  onClearLocationClick,
  selectedLocationId,
  onInfoClick,
  isSupervisor = false,
  onHeatSearch,
}: NavigationControlsProps) => {
  const {
    isLoggedIn,
    canManageCranes,
    showLogoutButton,
    onCraneSelectionClick,
    onLogoutClick,
    onLoginClick,
  } = authentication;

  const { isSearchActive, searchStatus, onSearchClick, onClearSearch } = search;
  const [menuOpen, setMenuOpen] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const [heatSearchValue, setHeatSearchValue] = useState("");

  const handleMenuToggle = () => {
    setMenuOpen(!menuOpen);
  };

  const handleMenuClose = () => {
    setMenuOpen(false);
  };

  const handleHeatSearch = () => {
    if (heatSearchValue.trim() && onHeatSearch) {
      onHeatSearch(heatSearchValue.trim());
    }
  };

  const handleHeatSearchKeyPress = (
    event: React.KeyboardEvent<HTMLDivElement>,
  ) => {
    if (event.key === "Enter") {
      handleHeatSearch();
    }
  };

  const handleClearHeatSearch = () => {
    setHeatSearchValue("");
    if (onClearSearch) {
      onClearSearch();
    }
  };

  return (
    <>
      {/* Pagination Controls - Horizontal Layout */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "8px",
          width: "100%",
        }}
      >
        {/* Previous Button */}
        <Button
          variant="contained"
          color="primary"
          sx={{
            minHeight: "70px",
            flex: "1",
            fontSize: "3rem",
            fontWeight: "bold",
            borderRadius: "8px",
            boxShadow: (theme) => theme.shadows[1],
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          onClick={() => onPageChange(-1)}
          disabled={regularLocationPage === 1}
        >
          <ChevronLeft />
        </Button>

        {/* Page Number */}
        <Box
          sx={{
            minWidth: "80px",
            minHeight: "70px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            fontWeight: "bold",
            fontSize: "24px",
            backgroundColor: (theme) =>
              theme.palette.billetYard.backgrounds.lighter,
            border: (theme) => `2px solid ${theme.palette.primary.main}`,
            borderRadius: "8px",
            color: (theme) => theme.palette.primary.main,
            boxShadow: (theme) => theme.shadows[1],
            userSelect: "none",
            WebkitUserSelect: "none",
            MozUserSelect: "none",
            msUserSelect: "none",
            flex: "0 0 auto",
          }}
        >
          {regularLocationPage}
        </Box>

        {/* Next Button */}
        <Button
          variant="contained"
          color="primary"
          sx={{
            minHeight: "70px",
            flex: "1",
            fontSize: "3rem",
            fontWeight: "bold",
            borderRadius: "8px",
            boxShadow: (theme) => theme.shadows[1],
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          onClick={() => onPageChange(1)}
          disabled={regularLocationPage === totalPages}
        >
          <ChevronRight />
        </Button>
      </Box>

      {/* Heat Search Input - Only visible for Supervisors */}
      {isSupervisor && (
        <Box
          sx={{
            display: "flex",
            width: "100%",
            marginTop: "16px",
            gap: "8px",
            alignItems: "center",
          }}
        >
          <TextField
            variant="outlined"
            placeholder="Heat Name"
            value={heatSearchValue}
            onChange={(e) => setHeatSearchValue(e.target.value)}
            onKeyPress={handleHeatSearchKeyPress}
            sx={{
              width: "70%",
              backgroundColor: (theme) => theme.palette.background.paper,
            }}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    {heatSearchValue && (
                      <IconButton
                        onClick={handleClearHeatSearch}
                        edge="end"
                        size="small"
                        sx={{ marginRight: "4px" }}
                      >
                        <ClearIcon />
                      </IconButton>
                    )}
                    <IconButton
                      onClick={handleHeatSearch}
                      edge="end"
                      color="primary"
                      disabled={!heatSearchValue.trim()}
                    >
                      <SearchIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
          />

          {/* Clear Search Button - Always visible, disabled when no search active */}
          <Button
            variant="contained"
            sx={{
              width: "30%",
              minHeight: "56px",
              fontSize: "1rem",
              fontWeight: "bold",
              borderRadius: "8px",
              boxShadow: (theme) => theme.shadows[1],
              backgroundColor: (theme) =>
                isSearchActive
                  ? theme.palette.primary.main
                  : theme.palette.grey[500],
              color: "white",
            }}
            onClick={onClearSearch}
            disabled={!isSearchActive}
          >
            Clear
          </Button>
        </Box>
      )}

      {/* Clear Search Button - Only visible when search is active and NOT supervisor */}
      {!isSupervisor && isSearchActive && onClearSearch && (
        <Box
          sx={{
            display: "flex",
            width: "100%",
            marginTop: "16px",
            marginBottom: "16px",
          }}
        >
          <Button
            variant="contained"
            color="secondary"
            sx={{
              minHeight: "56px",
              fontSize: "1rem",
              fontWeight: "bold",
              borderRadius: "8px",
              boxShadow: (theme) => theme.shadows[1],
              width: "100%",
              backgroundColor: (theme) => theme.palette.error.main,
              color: "white",
            }}
            onClick={onClearSearch}
            startIcon={<ClearIcon />}
          >
            Clear Search
          </Button>
        </Box>
      )}

      {/* Info Button - Visible when any location is selected */}
      {selectedLocationId && onInfoClick && (
        <Box
          sx={{
            display: "flex",
            width: "100%",
            marginTop: "16px",
            marginBottom: "16px",
          }}
        >
          <Button
            variant="contained"
            startIcon={<InfoIcon />}
            sx={{
              minHeight: "56px",
              fontSize: "1rem",
              fontWeight: "bold",
              borderRadius: "8px",
              boxShadow: (theme) => theme.shadows[1],
              width: "100%",
              backgroundColor: (theme) => theme.palette.success.main,
              color: "white",
            }}
            onClick={onInfoClick}
          >
            Info
          </Button>
        </Box>
      )}

      {/* Menu/Login Button - Positioned closer to bottom */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          width: "100%",
          marginTop: "auto",
          paddingTop: "8px",
        }}
      >
        {isLoggedIn ? (
          // Show Menu button when logged in
          <Button
            ref={menuButtonRef}
            variant="contained"
            color="primary"
            sx={{
              minHeight: "56px",
              fontSize: "1rem",
              fontWeight: "bold",
              borderRadius: "8px",
              boxShadow: (theme) => theme.shadows[1],
              width: "100%",
              backgroundColor: (theme) => theme.palette.primary.main,
            }}
            onClick={handleMenuToggle}
            startIcon={<MenuIcon />}
          >
            Menu
          </Button>
        ) : (
          // Show Login button when not logged in (Touch mode only)
          onLoginClick && (
            <Button
              variant="contained"
              color="secondary"
              sx={{
                minHeight: "56px",
                fontSize: "1rem",
                fontWeight: "bold",
                borderRadius: "8px",
                boxShadow: (theme) => theme.shadows[1],
                width: "100%",
                backgroundColor: (theme) => theme.palette.primary.light,
              }}
              onClick={onLoginClick}
              startIcon={<LoginIcon />}
            >
              Login
            </Button>
          )
        )}
      </Box>

      {/* Operator and Crane Display */}
      {/* <div style={{ marginTop: "8px" }}>
        <OperatorCraneDisplay
          selectedCraneId={selectedCraneId || null}
          selectedOperatorId={selectedOperatorId || null}
        /> 
      </div>*/}

      {/* Menu Drop Up */}
      <MenuDropUp
        open={menuOpen}
        onClose={handleMenuClose}
        onSearchClick={onSearchClick}
        onLocationEditionClick={onLocationEditionClick}
        onCraneSelectionClick={onCraneSelectionClick}
        onLogoutClick={onLogoutClick}
        onClearLocationClick={onClearLocationClick}
        canAccessLocationContentEdition={canAccessLocationContentEdition}
        canManageCranes={canManageCranes}
        showLogoutButton={showLogoutButton}
        isLoggedIn={isLoggedIn}
        showClearLocationButton={
          !!selectedLocationIdForClear && !!onClearLocationClick
        }
        anchorElement={menuButtonRef.current}
      />
    </>
  );
};
